/** @jsxImportSource theme-ui @jsxRuntime classic */

import * as Icons from '@tishman/icons';
import React, { Fragment, useMemo } from 'react';

import type { ReactNode } from 'react';

const DOUBLE_CURLIES_RE = /\{\{.+?\}\}/g;

const getIcon = (iconName: string) => {
  if (iconName === 'ticket') {
    return (
      <Icons.TicketIcon
        aria-hidden
        style={{ color: '#BEB1D7', width: '100%' }}
      />
    );
  }

  return '';
};

export interface InterpolatedStringProps {
  children: string;
  /**
   * How to render each "chunk" (i.e. string or interpolated element)
   *
   * @example
   *   (chunk) => {
   *     if (typeof chunk === 'string') {
   *       return <Text variant="heading">{chunk}</Text>
   *     }
   *
   *     return chunk;
   *   };
   */
  renderChunk?: (chunk: ReactNode, i: number) => ReactNode;
}

/**
 * Renders a string containing interpolated elements in curly-brace
 * (i.e. [Liquid](https://shopify.dev/api/liquid)) syntax.
 *
 * Supported filters:
 *   * `icon`
 *     * Supported inputs:
 *       * `"ticket"`
 *
 * @example
 *   <InterpolatedString>
 *     {"This is an icon: {{ 'ticket' | icon }}"}
 *   </InterpolatedString>
 */
export const InterpolatedString = ({
  children: s,
  renderChunk = (chunk) => chunk,
}: InterpolatedStringProps): JSX.Element => {
  if (typeof s !== 'string') {
    throw new TypeError('InterpolatedString: expected one string child');
  }

  const chunks = useMemo(() => {
    const matches = [...s.matchAll(DOUBLE_CURLIES_RE)];

    return [
      s.slice(0, matches[0]?.index),
      ...matches.flatMap((match) => {
        const match0 = match[0];

        const s = match0.slice(2, -2).trim();

        let element: string | JSX.Element = s.split('|')[0].trim();

        const [, ...filters] = s.split('|');
        for (const filter of filters) {
          switch (filter.trim()) {
            case 'icon': {
              const iconName =
                typeof element === 'string'
                  ? element.replace(/^['"]/, '').replace(/'$/, '')
                  : '';
              element = getIcon(iconName);

              break;
            }
            default: {
              console.warn(`unsupported filter: ${JSON.stringify(filter)}`);
              break;
            }
          }
        }

        return [element, match.input!.slice(match.index! + match0.length)];
      }),
    ]
      .filter(Boolean)
      .map((chunk, i) => <Fragment key={i}>{renderChunk(chunk, i)}</Fragment>);
  }, [s, renderChunk]);

  return <Fragment>{chunks}</Fragment>;
};
